import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  getDocs,
  serverTimestamp,
  onSnapshot,
  writeBatch,
  type Unsubscribe,
} from 'firebase/firestore'
import { db } from '@/services/firebase/config'
import type { Notification, CreateNotificationData } from '@/types/notification'

const NOTIFICATIONS_COLLECTION = 'notifications'

/**
 * Subscribe to a user's notifications in real-time.
 * Returns the 50 most recent, ordered by createdAt desc (client-side sort
 * to avoid composite index requirement on userId + createdAt).
 */
export function subscribeToNotifications(
  userId: string,
  callback: (notifications: Notification[]) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const q = query(
    collection(db, NOTIFICATIONS_COLLECTION),
    where('userId', '==', userId)
  )

  return onSnapshot(
    q,
    (snapshot) => {
      const notifications = snapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      })) as Notification[]

      // Sort client-side by createdAt desc, limit to 50
      notifications.sort((a, b) => {
        const aTime = a.createdAt?.toMillis?.() ?? 0
        const bTime = b.createdAt?.toMillis?.() ?? 0
        return bTime - aTime
      })

      callback(notifications.slice(0, 50))
    },
    (error) => {
      console.error('subscribeToNotifications error:', error)
      onError?.(error)
      callback([])
    }
  )
}

/**
 * Mark a single notification as read.
 */
export async function markAsRead(notificationId: string): Promise<void> {
  await updateDoc(doc(db, NOTIFICATIONS_COLLECTION, notificationId), {
    read: true,
  })
}

/**
 * Mark all of a user's unread notifications as read (batch update).
 */
export async function markAllAsRead(userId: string): Promise<void> {
  const q = query(
    collection(db, NOTIFICATIONS_COLLECTION),
    where('userId', '==', userId),
    where('read', '==', false)
  )

  const snapshot = await getDocs(q)
  if (snapshot.empty) return

  const batch = writeBatch(db)
  snapshot.docs.forEach((d) => {
    batch.update(d.ref, { read: true })
  })
  await batch.commit()
}

/**
 * Delete a single notification.
 */
export async function deleteNotification(notificationId: string): Promise<void> {
  await deleteDoc(doc(db, NOTIFICATIONS_COLLECTION, notificationId))
}

/**
 * Create a notification document (for client-side triggered notifications).
 */
export async function createNotification(data: CreateNotificationData): Promise<string> {
  const docRef = await addDoc(collection(db, NOTIFICATIONS_COLLECTION), {
    userId: data.userId,
    type: data.type,
    title: data.title,
    message: data.message,
    read: false,
    link: data.link ?? null,
    relatedId: data.relatedId ?? null,
    createdAt: serverTimestamp(),
  })
  return docRef.id
}

/**
 * Get the count of unread notifications for a user.
 */
export async function getUnreadCount(userId: string): Promise<number> {
  const q = query(
    collection(db, NOTIFICATIONS_COLLECTION),
    where('userId', '==', userId),
    where('read', '==', false)
  )

  const snapshot = await getDocs(q)
  return snapshot.size
}
